import React, { useEffect, useRef, useState } from 'react';
import {
    AirportForm,
    BedroomForm,
    CustomDateSelectionCalendar,
    FeatureForm,
    ImagesForm,
    InformationForm,
    ListingCard,
    ListingTypeSelect,
    LocationForm,
    PricingForm,
    SubmitAndDraftSection,
    TermsAndRules,
} from './components';
import { useFormContext } from '@/providers';
import './listing.css';
import Swal from 'sweetalert2';

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Error Boundary caught an error:", error, errorInfo);
        // You can log the error to an error reporting service here
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-6 text-red-600 bg-red-100 rounded-md">
                    <h2 className="text-xl font-bold">Something went wrong!</h2>
                    <p className="my-2">{this.state.error?.message || 'Unknown error occurred'}</p>
                    <button
                        onClick={this.handleReset}
                        className="px-4 py-2 mt-4 text-white bg-red-600 rounded hover:bg-red-700"
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export const ListingTypeForm = () => {
    const { handleSubmit, errors, watch } = useFormContext();
    const autoCompleteRef = useRef(null);
    const formRef = useRef(null);

    const [drag, setDrag] = useState({
        lat: parseFloat(watch('latitude')) || 39.5,
        lng: parseFloat(watch('longitude')) || -100.0,
    });

    const [selectedLocation, setSelectedLocation] = useState({
        lat: parseFloat(watch('latitude')) || 39.5,
        lng: parseFloat(watch('longitude')) || -100.0,
    });

    useEffect(() => {
        console.log(errors)
        if (!errors || Object.keys(errors).length === 0) return;

        // Function to recursively find the first error message
        const findFirstErrorMessage = (errorObj) => {
            if (errorObj.message) {
                return errorObj.message;
            }

            if (Array.isArray(errorObj)) {
                for (const item of errorObj) {
                    if (item && typeof item === 'object') {
                        const message = findFirstErrorMessage(item);
                        if (message) return message;
                    }
                }
            } else if (typeof errorObj === 'object') {
                for (const key in errorObj) {
                    const message = findFirstErrorMessage(errorObj[key]);
                    if (message) return message;
                }
            }

            return null;
        };

        const firstErrorMessage = findFirstErrorMessage(errors);

        if (firstErrorMessage) {
            Swal.fire({
                icon: 'error',
                text: firstErrorMessage,
                confirmButtonColor: '#d33',
            });
        } else {
            Swal.fire({
                icon: 'error',
                text: 'Please correct the errors in the form',
                confirmButtonColor: '#d33',
            });
        }
    }, [errors]);

    return (
        <ErrorBoundary>
            <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 p-6 md:grid-cols-12">
                <div className="md:col-span-9">
                    <div className="mt-1">
                        <ListingTypeSelect />
                        <LocationForm
                            setSelectedLocation={setSelectedLocation}
                            selectedLocation={selectedLocation}
                            drag={drag}
                            setDrag={setDrag}
                            autoCompleteRef={autoCompleteRef}
                        />
                        <ImagesForm />
                        <InformationForm />
                        <BedroomForm />
                        <AirportForm />
                        <PricingForm />
                        <FeatureForm />
                        <TermsAndRules />
                        <CustomDateSelectionCalendar />
                        <SubmitAndDraftSection />
                    </div>
                </div>

                <div className="flex justify-end md:col-span-3 h-[200px] sticky !top-[15%]">
                    <ListingCard />
                </div>
            </form>
        </ErrorBoundary>
    );
};